import axios from "../plugins/axios";
import localService from "../services/localService";
import { getField, updateField } from "vuex-map-fields";
import { vm } from "../main";
import { uuid } from "vue-uuid";
import { objectToQueryString } from "@/helpers/index";

const MAX_CACHE_SIZE = 20;

export default {
  namespaced: true,
  state: () => ({
    /* Static Data */
    tableHeaders: [
      {
        text: "Name",
        value: "name",
        sortable: true,
        filterable: false,
        align: "left",
      },
      {
        text: "Email",
        value: "email",
        sortable: true,
        filterable: true,
        align: "left",
      },
      {
        text: "Company",
        value: "organization_name",
        sortable: true,
        filterable: false,
        align: "left",
      },
      {
        text: "",
        value: "options",
        sortable: false,
        filterable: false,
        align: "right",
      },
    ],
    userTableHeaders: [
      {
        text: "Name",
        value: "name",
        sortable: true,
        filterable: false,
        align: "left",
      },
      {
        text: "Email",
        value: "email",
        sortable: true,
        filterable: true,
        align: "left",
      },
      {
        text: "Role",
        value: "client_role",
        sortable: true,
        filterable: true,
        align: "left",
      },
      {
        text: "",
        value: "options",
        sortable: false,
        filterable: false,
        align: "right",
      },
    ],
    /* Static Data */

    /* State */
    currentOrganization: {},
    email: null,
    name: null,
    role: null,
    company: null,
    organization: null,
    website: null,
    users: [],
    clientUsersQueryCacheMap: new Map(),
    clientUserLoading: true,
    clients: [],
    organizations: [],
    brokerOrganizations: [],
    organizationIntent: null,
    roles: ["Viewer", "Contributor", "Admin"],
    delegateInvitedMembers: [],
    clientDelegations: [],
    clientDelegationsLoading: true,
    totalPayableAmount: 499,

    // Company users
    selectedJoinedFilters: [],
    selectedStatusFilters: [],
    selectedSystemFilter: false,
    userInfoDrawer: false,
    selectedUser: null,
    deleteDialog: false,
    userSettings: {},
    frpStatus: {},
  }),
  mutations: {
    updateField,
    setState(state, payload) {
      state[payload.key] = payload.value;
    },

    setFrpStatus(state, payload) {
      state.frpStatus = payload;
    },

    deleteOrganizationClients: (state, client) => {
      const foundIndex = state.users.findIndex((x) => x.entity_id == client.entity_id);
      state.users.splice(foundIndex, 1);
    },

    updateOrganizationClients: (state, client) => {
      const foundIndex = state.users.findIndex((x) => x.entity_id == client.entity_id);

      if (foundIndex == -1) {
        state.users.push(client);
      } else {
        Vue.set(state.users, foundIndex, client);
      }
    },

    updateBrokersPurchases(state, payload) {
      const foundIndex = state.brokerOrganizations.findIndex((x) => x.entity_id == payload.organization_id);
      if (!state.brokerOrganizations[foundIndex].purchases.length) {
        state.brokerOrganizations[foundIndex].purchases = [];
      }
      state.brokerOrganizations[foundIndex].purchases.push(payload);
    },

    updateInvitedDelegateMembers(state, payload) {
      state.delegateInvitedMembers.push(payload);
    },

    updateClientList(state, { data, query }) {
      state.clients = data.results;

      if (state.clientUsersQueryCacheMap.size >= MAX_CACHE_SIZE) {
        // Remove the oldest entry (first inserted key)
        const oldestKey = state.clientUsersQueryCacheMap.keys().next().value;
        state.clientUsersQueryCacheMap.delete(oldestKey);
      }

      state.clientUsersQueryCacheMap.set(query, data);
    },

    updateUserDetails(state, data) {
      //using for update company user details from super admin
      if (state.selectedUser?.entity_id) {
        state.clients.map((e) => {
          if (e.organization_id == state.selectedUser.organization_id) {
            e.is_primary_contact = 0;
          }
          return e;
        });
        let index = state.clients.findIndex((e) => e.entity_id == state.selectedUser.entity_id);
        let selectedUser = { ...state.selectedUser };
        selectedUser.name = data.name;
        selectedUser.email = data.email;
        selectedUser.is_primary_contact = data.is_primary_contact;
        selectedUser.organization_name = data.organization_name;
        selectedUser.organization_id = data.organization_id;
        vm.$set(state.clients, index, selectedUser);
        state.selectedUser = data;
      }
    },
  },
  actions: {
    async getCurrentOrganization({ commit }) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.get("/me/organization");
        if (!data.success) {
          return;
        }
        commit("setState", { key: "currentOrganization", value: data.organization });
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async getOrganizations({ commit }) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.get("/organizations");
        if (!data.success) {
          return;
        }
        commit("setState", { key: "organizations", value: data.results });
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async getBrokerOrganizations({ commit }) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.get("/broker_organization_info");
        if (!data.success) {
          return;
        }
        commit("setState", {
          key: "brokerOrganizations",
          value: data.organizations,
        });
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async getClients({ commit, state, rootState }, payload) {
      try {
        let url = "/client";
        let query = objectToQueryString(payload ?? {});

        let cacheHit = state.clientUsersQueryCacheMap.get(query);
        if (cacheHit) {
          commit("setState", { key: "clients", value: cacheHit.results });
          commit(
            "admin/setState",
            {
              key: "companyUsersApiStatus",
              value: {
                ...rootState.admin.companyUsersApiStatus,
                totalCount: cacheHit.total_count,
                totalPages: Math.ceil(cacheHit.total_count / 30),
              },
            },
            { root: true }
          );
          return;
        }

        url += "?" + query;

        const { data } = await axios.get(url);
        if (!data.success) {
          return;
        }
        commit(
          "admin/setState",
          {
            key: "companyUsersApiStatus",
            value: {
              ...rootState.admin.companyUsersApiStatus,
              totalCount: data.total_count,
              totalPages: Math.ceil(data.total_count / 30),
            },
          },
          { root: true }
        );
        commit("updateClientList", { data: data, query: query });
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async updateProfilePicture({ commit, dispatch, rootState }, payload) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.put("/update_profile_picture", payload);
        if (!data.success) {
          return;
        }
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        let currentUser = rootState.login.currentUser;
        currentUser.user = { ...currentUser.user, ...data.user };
        commit("setState", { key: "login/currentUser", value: currentUser });
        localService.setItem("userInfo", currentUser);
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async updateUser({ commit, dispatch, rootState }, payload) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.put("/update_username", payload);
        if (!data.success) {
          return;
        }
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        let currentUser = rootState.login.currentUser;
        currentUser.user = { ...currentUser.user, name: data.user.name };
        commit("setState", { key: "login/currentUser", value: currentUser });
        localService.setItem("userInfo", currentUser);
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async createClient({ commit, dispatch }, payload) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.post("/client", payload);
        if (!data.success) {
          return;
        }
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        commit("setState", { key: "name", value: null });
        commit("setState", { key: "email", value: null });
        commit("setState", { key: "role", value: null });
        if (payload.clickwraps) {
          dispatch(
            "clickwrap/newUserAssignClickwrap",
            {
              user_id: data.user.entity_id,
              clickwraps: payload.clickwraps.map((clickwrap) => clickwrap.entity_id),
            },
            { root: true }
          );
        }
        dispatch("getClients");
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async deleteClient({ dispatch }, payload) {
      try {
        const { data } = await axios.delete(`/client/${payload.entity_id}`);
        if (!data.success) return;
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        return data;
      } catch (e) {
        console.error(e);
      }
    },
    async getClientUsers({ commit }, organizationId = null) {
      try {
        commit("startLoading", null, { root: true });
        let url = "/organization_management/client";
        if (organizationId) {
          url = `/organization_management/${organizationId}/client`;
        }
        const { data } = await axios.get(url);
        if (!data.success) {
          return;
        }
        commit("setState", { key: "users", value: data.results });
        commit("setState", { key: "clientUserLoading", value: false });
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async createClientUser({ commit, dispatch, rootState, state }, payload) {
      try {
        commit("startLoading", null, { root: true });
        let payloadData = { ...payload };
        if (payloadData.role) {
          payloadData.role = payload.role.toLowerCase();
        }
        let customSuccessNotification = false;
        if (payloadData.custom_success_notification) {
          customSuccessNotification = true;
          delete payloadData.custom_success_notification;
        }
        const { data } = await axios.post("/organization_management/client", payloadData);
        if (!data.success) {
          return;
        }
        if (customSuccessNotification) {
          dispatch(
            "showMessage",
            { message: `${payloadData.name} was invited`, messageType: "success", messageTitle: "Success" },
            { root: true }
          );
        } else {
          dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        }
        commit("setState", { key: "name", value: null });
        commit("setState", { key: "email", value: null });
        commit("setState", { key: "role", value: null });
        dispatch("getClientUsers");
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async inviteClientBulkUser({ commit }, payload) {
      const { data } = await axios.post("/organization_management/clients", payload);
      if (!data.success) {
        return;
      }
      return data;
    },
    async deleteClientUser({ commit, dispatch, state }, payload) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.delete(`/organization_management/client/${payload.entity_id}`);
        if (!data.success) {
          return;
        }
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        dispatch("getClientUsers");
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async getClientAllDelegations({ commit }, organizationId = null) {
      let url = "/organization_management/client_delegations";
      if (organizationId) {
        url = `/organization_management/${organizationId}/client_delegations`;
      }
      const { data } = await axios.get(url);
      commit("setState", {
        key: "clientDelegations",
        value: data.delegations || [],
      });
      commit("setState", { key: "clientDelegationsLoading", value: false });
    },
    async setUserOnboard({ commit, dispatch }, payload) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.post("/user_onboard", payload);
        if (!data.success) {
          return;
        }

        let userInfo = localService.getItem("userInfo");
        userInfo.user.client_title = payload.client_title;
        if (userInfo.user.organization_user_id == userInfo.user.entity_id) {
          userInfo.user.organization_responsibility = payload.intent;
          userInfo.user.coverage_due_date = payload.coverage_due_date;
        } else {
          userInfo.user.organization_responsibility = payload.organization_responsibility;
        }
        localService.setItem("userInfo", userInfo);
        commit("login/setState", { key: "currentUser", value: userInfo }, { root: true });

        return { success: true };
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async completeGetStartedUserOverlay({ commit, dispatch }, payload) {
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.post("/user_onboard", payload);
        if (!data.success) {
          return;
        }
        let userInfo = localService.getItem("userInfo");
        userInfo.user.client_title = payload.client_title;
        if (userInfo.user.organization_user_id == userInfo.user.entity_id) {
          userInfo.user.intent = payload.intent;
          userInfo.user.organization_responsibility = payload.intent;
          userInfo.user.coverage_due_date = payload.coverage_due_date;
        } else {
          userInfo.user.organization_responsibility = payload.organization_responsibility;
        }
        localService.setItem("userInfo", userInfo);
        commit("login/setState", { key: "currentUser", value: userInfo }, { root: true });
        if (userInfo?.user?.role == "broker") {
          vm.$nextTick(() => {
            vm.$router.push({ path: "/broker/members" });
          });
        } else if (userInfo?.user?.role == "client" && userInfo?.user?.client_role == "admin") {
          vm.$nextTick(() => {
            vm.$router.push({ path: "/user/getting-started/delegate" });
          });
        } else {
          vm.$nextTick(() => {
            vm.$router.push({ path: "/user/readiness-checklist" });
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async inviteDelegateTeamMember({ commit, dispatch }, payload) {
      let data = { ...payload };
      data.entity_id = uuid.v4();
      commit("updateInvitedDelegateMembers", data);
      return data;
    },

    deleteOrganizationClients({ commit }, payload) {
      commit("deleteOrganizationClients", payload);
    },

    updateOrganizationClients({ commit }, payload) {
      commit("updateOrganizationClients", payload);
    },

    async getQuickStartStatus({ commit }, payload) {
      const { data } = await axios.get("/company_setup/status/quick_start");
      if (!data.success) {
        return;
      }
      return data;
    },

    async getUserSettings({ commit }) {
      const { data } = await axios.get("settings");
      commit("setState", { key: "userSettings", value: data.settings });
      return data;
    },

    async saveUserSettings({ commit }, payload) {
      const { data } = await axios.post("settings", payload);
      if (!data.success) return;
      commit("setState", { key: "userSettings", value: data.settings });
      return data;
    },

    async trackPolicyClick({ commit }, handle) {
      const { data } = await axios.post(`/track/policies/${handle}`);
      if (!data.success) return;
      return data;
    },

    async updateClientDetails({ commit, dispatch }, payload) {
      // using for update details such as email, org_id etc. of company user from super admin
      let payloadData = { ...payload };
      const entityId = payload.entity_id;
      delete payload.entity_id;
      const { data } = await axios.post(`/client/${entityId}`, payloadData);
      if (!data.success) {
        dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        return;
      }
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      commit("updateUserDetails", data.user);
      return data;
    },

    async declineCoverage({ commit, dispatch }, payload) {
      const { data } = await axios.post("decline_coverage", payload);
      if (!data.success) return;
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return data;
    },

    async getFrpStatus({ commit }, organizationId = null) {
      let endpoint = "/frp_status";
      if (organizationId) endpoint += `/${organizationId}`;
      const { data } = await axios.get(endpoint);
      if (!data.success) return;
      commit("setFrpStatus", data);
      return data;
    },
  },

  getters: {
    getField,
    clientTableHeaders: (state) => state.tableHeaders,
    clients: (state) => state.clients,
    organizations: (state) => state.organizations,
    brokerOrganizations: (state) => state.brokerOrganizations,
    userTableHeaders: (state) => state.userTableHeaders,
    adminClientTableHeaders: (state) => state.adminClientTableHeaders,
    users: (state) => state.users,
    organizationIntent: (state) => state.organizationIntent,
  },
};
